import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import LoginView from "@/views/auth/LoginView.vue";
import RecoverView from "@/views/auth/RecoverView.vue";

import BlockList from "@/views/auth/BlockList.vue";
import PublicLayout from "@/components/layout/PublicLayout.vue";
import ResidentRegisterPublic from "@/views/queries/censusMenu/residents/ResidentRegisterPublic.vue";
import { AuthService } from "@/services/auth/AuthServices";
import ResidentRegisterSuccess from "@/views/queries/censusMenu/residents/ResidentRegisterSuccess.vue";
import { useAuthStore } from "@/stores/auth";

const routes: Array<RouteRecordRaw> = [
  { path: "/", name: "login", component: LoginView, meta: { requiresAuth: false } },
  { path: "/reset-password/:token", name: "reset-password", component: RecoverView, meta: { requiresAuth: false } },  
  {
    path: "/public",
    component: PublicLayout,
    meta: { requiresAuth: false },
    children: [
      { path: "register-resident/:formUUID", name: "register-resident", component: ResidentRegisterPublic },
      { path: 'register-resident/success', name : 'register-resident-success', component: ResidentRegisterSuccess}
    ],
  },
  {
    path: "/block-list",
    name: "block-list",
    component: BlockList,
    meta: { requiresAuth: true },
  },
  {
    path: "/community/:community/block/:id",
    meta: { requiresAuth: true },
    children: [
      { path: "home", name: "home", component: () => import("@/views/menu/HomeView.vue"), props: true },
      { path: "events", name: "events", component: () => import("@/views/principal/EventsView.vue") },
      { path: "emergency", name: "emergency", component: () => import("@/views/principal/EmergencyView.vue") },
      {
        path: "emergency/:emergencyId",
        children: [
          { path: "simulacrum", name: "simulacrum", component: () => import("@/views/Emergency/SimulacrumView.vue") },
          {
            path: "alert/:emergencyTypeId",
            children: [
              { path: "", name: "alert", component: () => import("@/views/Emergency/alert/AlertView.vue") },
              { path: "success", name: "alert-success", component: () => import("@/views/Emergency/alert/AlertSuccessView.vue") },
              { path: "success/simulacrum", name: "alert-success-simulacrum", component: () => import("@/views/Emergency/alert/AlertSuccessView.vue"), props: { simulacrum: true } },
              { path: "step/:stepId", name: "alert-step", component: () => import("@/views/Emergency/alert/AlertStepView.vue") },
              { path: "step/:stepId/simulacrum", name: "alert-step-simulacrum", component: () => import("@/views/Emergency/alert/AlertStepView.vue"), props: { simulacrum: "true" } },
              { path: "instruction", name: "instruction", component: () => import("@/views/Emergency/instruction/InstructionView.vue") },
            ],
          },
        ],
      },
      {
        path: "tutorial",
        children: [
          { path: "", name: "tutorial", component: () => import("@/views/principal/TutorialView.vue") },
          { path: "video", name: "movie", component: () => import("@/views/tutorial/VideoTutorialView.vue") },
        ],
      },
      {
        path: "queries",
        children: [
          { path: "", name: "queries", component: () => import("@/views/principal/QueriesView.vue") },
          { path: "questions", name: "questions", component: () => import("@/components/queries/organisms/questions.vue") },
          { path: "buildingBackground", name: "buildingBackground", component: () => import("@/components/queries/organisms/buildingBackground.vue") },
          { path: "contractors", name: "contractors", component: () => import("@/views/queries/contractorsView.vue") },
          { path: "fireInsurance", name: "fireInsurance", component: () => import("@/views/queries/fireInsurance.vue") },
          {
            path: "residents",
            children: [
              { path: "", name: "residents", component: () => import("@/views/queries/censusMenu/residents/ResidentsView.vue") },
              { path: "create", name: "resident-create", component: () => import("@/views/queries/censusMenu/residents/ResidentCreate.vue") },
              { path: ":residentId", name: "resident-show", component: () => import("@/views/queries/censusMenu/residents/ResidentShow.vue") },
              { path: ":residentId/edit", name: "resident-edit", component: () => import("@/views/queries/censusMenu/residents/ResidentEdit.vue") },
            ],
          },
          {
            path: "co-owners",
            children: [
              { path: "", name: "co-owners", component: () => import("@/views/queries/censusMenu/co-owners/CoOwnersView.vue") },
              { path: "create", name: "co-owner-create", component: () => import("@/views/queries/censusMenu/co-owners/CoOwnerCreate.vue") },
              { path: ":userId", name: "co-owner-show", component: () => import("@/views/queries/censusMenu/co-owners/CoOwnerShow.vue") },
              { path: ":userId/edit", name: "co-owner-edit", component: () => import("@/views/queries/censusMenu/co-owners/CoOwnerEdit.vue") },
            ],
          },
          {
            path: "census",
            children: [
              { path: "", name: "census", component: () => import("@/views/queries/CensusView.vue") },
              { path: "diminished-capacities", name: "DiminishedCapacities", component: () => import("@/views/queries/censusMenu/DiminishedCapacitiesView.vue") },
              { path: "fire-extinguisher", name: "FireExtinguisher", component: () => import("@/views/queries/censusMenu/FireExtinguisherView.vue") },
              { path: "gas-cylinders", name: "GasCylinders", component: () => import("@/views/queries/censusMenu/GasCylindersView.vue") },
              { path: "pets", name: "Pets", component: () => import("@/views/queries/censusMenu/PetsView.vue") },
              { path: "vehicles", name: "Vehicles", component: () => import("@/views/queries/censusMenu/VehiclesView.vue") },
              { path: "certifications", name: "Certifications", component: () => import("@/views/queries/censusMenu/CertificationsView.vue") },
              { path: "emergency-plan", name: "EmergencyPlan", component: () => import("@/views/queries/censusMenu/EmergencyPlanView.vue") },
            ],
          },
          {
            path: "committe",
            children: [
              { path: "", name: "committe", component: () => import("@/views/queries/CommitteMenuView.vue") },
              { path: "members", name: "committee-members", component: () => import("@/views/queries/committeeMenu/CommitteView.vue") },
              { path: "personal-service", name: "servicio-personal", component: () => import("@/views/queries/committeeMenu/PersonalServiceView.vue") },
              { path: "personal-service/workers", name: "trabajadores", component: () => import("@/views/queries/committeeMenu/WorkersView.vue") },
            ],
          },
          { path: "reglamentos", name: "reglamentos", component: () => import("@/views/tutorial/RegulationView.vue") },
          { path: "planos", name: "planos", component: () => import("@/views/planos/PlanosView.vue") },
        ],
      },
      
      
    ],
    
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/404.vue'),
    meta: { requiresAuth: false }
  }
  
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const authService = new AuthService();
  const isAuthenticated = await authService.hasValidUserSession();
  
  if (!to.matched.length) {
    // Ruta no encontrada
    if (isAuthenticated) {
      return next({ name: 'NotFound' });
    } else {
      return next({ name: 'login' });
    }
  }
  
  if (to.matched.some((record) => record.meta?.requiresAuth)) {
    if (!isAuthenticated) {
      return next({ name: "login" });
    }
  }
  
  next();
});


export default router;
